import { Icon } from "react-migration/components/Icon";
import { LtIcons } from "@landtechnologies/icons/font/lt-icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import React, { PropsWithChildren } from "react";

function Label({ children }: PropsWithChildren) {
  return <DropdownMenu.Label className="text-xs uppercase text-blue-grey-950/50">{children}</DropdownMenu.Label>;
}

interface ItemProps extends DropdownMenu.MenuItemProps {
  leadIcon?: `${LtIcons}`;
  leadComponent?: React.ReactNode;
  tailComponent?: React.ReactNode;
  tailAction?: React.ReactNode;
}

function Item({ children, leadIcon, leadComponent, tailComponent, tailAction, ...props }: ItemProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      {[!!leadComponent, !!leadIcon, !!children].some(Boolean) && (
        <DropdownMenu.Item
          {...props}
          className="flex items-center justify-between min-w-0 gap-2 p-1 cursor-pointer grow outline-hidden rounded-xs hover:bg-platform-blue-50"
        >
          <div className="flex items-center w-full min-w-0 gap-2">
            {leadComponent && <div className="shrink-0">{leadComponent}</div>}
            {leadIcon && (
              <div>
                <Icon icon={`icon-${leadIcon}`} size="md" />
              </div>
            )}
            <div className="text-sm truncate text-blue-grey-950 grow">{children}</div>
          </div>
          {tailComponent}
        </DropdownMenu.Item>
      )}
      {tailAction}
    </div>
  );
}

interface SubTriggerProps extends DropdownMenu.DropdownMenuSubTriggerProps {
  tailIcon?: `${LtIcons}`;
}

function SubTrigger({ children, tailIcon = "lt-arrow-right-s-line", ...props }: SubTriggerProps) {
  return (
    <DropdownMenu.SubTrigger
      className="flex justify-between p-1 rounded-xs outline-hidden hover:bg-platform-blue-50"
      {...props}
    >
      <div className="text-sm">{children}</div>
      <div className="text-blue-grey-300">
        <Icon icon={`icon-${tailIcon}`} size="sm" />
      </div>
    </DropdownMenu.SubTrigger>
  );
}

function Content(props: DropdownMenu.MenuContentProps) {
  return (
    <DropdownMenu.Content
      className="bg-white rounded-md p-2 drop-shadow-lg min-w-[260px] max-w-[260px] border-blue-grey-100"
      {...props}
    />
  );
}

function SubContent(props: DropdownMenu.MenuSubContentProps) {
  return (
    <DropdownMenu.SubContent
      className="bg-white max-h-[350px] overflow-y-scroll rounded-md p-2 drop-shadow-lg min-w-[260px] max-w-[260px] border-blue-grey-100"
      {...props}
    />
  );
}

interface GroupProps extends PropsWithChildren {
  title: string;
}

function Group({ children, title }: GroupProps) {
  return (
    <div className="flex flex-col gap-2">
      <RightClick.Label>{title}</RightClick.Label>
      <div>{children}</div>
    </div>
  );
}

export const RightClick = {
  ...DropdownMenu,
  Group,
  Item,
  Label,
  Content,
  SubTrigger,
  SubContent,
};
